import { useWindowDimensions } from "@luce/core";
import { useNavigation } from "@luce/core/adapters/navigation";
import { Box, Card, Divider, ScreenLayout, Tag } from "@luce/ui";

import { InvoicePaymentStatusEnum } from "../../__generated__/graphql";
import { PageHeader, usePullToRefresh } from "../../shared";
import { BACK_TO } from "../../shared/lib";
import { getInvoicePdfPath, getReceiptPdfPath, GST_REGISTRATION } from "../constant";
import { useInvoiceById } from "../gqlhooks/use-invoice-by-id";
import {
  formatInvoiceDate,
  getInvoiceStatusTag,
  getPdfToken,
  shouldShowPaymentProof,
} from "../lib";
import { InvoiceDetailFallback } from "./components/invoice-detail-fallback";
import { InvoiceDetailRow } from "./components/invoice-detail-row";
import { InvoiceDetailSkeleton } from "./components/invoice-detail-skeleton";
import { InvoiceDocumentRow } from "./components/invoice-document-row";
import { InvoiceLineItems } from "./components/invoice-line-items";
import { InvoicePaymentSummary } from "./components/invoice-payment-summary";
import { InvoiceVoucherBanner } from "./components/invoice-voucher-banner";
import { PaymentProofCard } from "./components/payment-proof-card";
import type { InvoiceDetailProps } from "./types";

const TITLE = "Invoice Details";
const DATE_FORMAT = "dd MMM yyyy";

const VOUCHER_STATUSES = [InvoicePaymentStatusEnum.Unpaid, InvoicePaymentStatusEnum.Underpaid];

export function InvoiceDetailScreen({ invoiceId, onOpenVouchers, payAction }: InvoiceDetailProps) {
  const { isDesktop } = useWindowDimensions();
  const { goBack } = useNavigation();
  const { invoice, loading, error, refetch } = useInvoiceById(invoiceId);
  const refresh = usePullToRefresh(() => refetch());

  let content;
  if (loading && !invoice) {
    content = <InvoiceDetailSkeleton />;
  } else if (!invoice) {
    content = error ? (
      <InvoiceDetailFallback
        message="Something went wrong loading your invoice."
        onRetry={() => void refetch()}
      />
    ) : (
      <InvoiceDetailFallback message="This invoice isn't available." />
    );
  } else {
    const statusTag = getInvoiceStatusTag(invoice);
    const isPaid = invoice.paymentStatus === InvoicePaymentStatusEnum.Paid;
    const invoiceToken = getPdfToken(invoice.pdfUrl);
    const receiptToken = getPdfToken(invoice.pdfReceiptUrl);

    content = (
      <Box className="gap-spacing-lg flex flex-col">
        {shouldShowPaymentProof(invoice) && (
          <PaymentProofCard
            invoiceId={invoice.id}
            uploadedUrl={invoice.paymentProofFile?.downloadUrl ?? null}
          />
        )}

        <Box className="gap-spacing-sm flex flex-col">
          <InvoiceDocumentRow
            title={`Invoice #${invoice.id}`}
            pdfPath={invoiceToken ? getInvoicePdfPath(invoice.id, invoiceToken) : null}
          />
          {isPaid && (
            <InvoiceDocumentRow
              title={`Receipt #${invoice.id}`}
              pdfPath={receiptToken ? getReceiptPdfPath(invoice.id, receiptToken) : null}
            />
          )}
          <InvoiceDetailRow label="Status">
            <Tag variant={statusTag.variant} size="sm" outline>
              {statusTag.label}
            </Tag>
          </InvoiceDetailRow>
          <InvoiceDetailRow label="Issued on">
            {formatInvoiceDate(invoice.issueDate, DATE_FORMAT)}
          </InvoiceDetailRow>
          <InvoiceDetailRow label="GST Registration No">{GST_REGISTRATION}</InvoiceDetailRow>
          <InvoiceDetailRow label="Due Date" divider={false}>
            {formatInvoiceDate(invoice.dueDate, DATE_FORMAT)}
          </InvoiceDetailRow>
        </Box>

        <Divider />
        <InvoiceLineItems lineItems={invoice.lineItems} />
        <Divider />
        <InvoicePaymentSummary invoice={invoice} />
      </Box>
    );
  }

  const voucherContent =
    invoice && onOpenVouchers && VOUCHER_STATUSES.includes(invoice.paymentStatus) ? (
      <InvoiceVoucherBanner invoice={invoice} onOpenVouchers={onOpenVouchers} />
    ) : null;

  const isProcessing = invoice ? getInvoiceStatusTag(invoice).label === "Processing" : false;
  const visiblePayAction = isProcessing ? undefined : payAction;

  const showBottomActions = !isDesktop && Boolean(voucherContent || visiblePayAction);

  return (
    <ScreenLayout>
      <ScreenLayout.Header title={TITLE} onBack={() => goBack(BACK_TO.invoices)} variant="white" />

      <ScreenLayout.Body
        {...(isDesktop ? { scrollable: false as const } : { scrollable: true as const, refresh })}
      >
        <Box className="gap-spacing-lg p-spacing-md flex flex-col md:p-0">
          {isDesktop && (
            <PageHeader
              onBack={() => goBack(BACK_TO.invoices)}
              title={TITLE}
              trailing={visiblePayAction}
            />
          )}
          {isDesktop && voucherContent}

          {/* A section on phones, a card from 768. Flush doesn't clip on phones,
              so the Copy buttons' tap areas reach past its top edge */}
          <Card gap="none" layout="flush">
            {content}
          </Card>
        </Box>
      </ScreenLayout.Body>

      {showBottomActions && (
        <ScreenLayout.Actions surface="shadow" className="gap-spacing-md flex flex-col">
          {voucherContent}
          {visiblePayAction}
        </ScreenLayout.Actions>
      )}
    </ScreenLayout>
  );
}
